import { AfterViewInit, Component, DestroyRef, inject, input, OnInit, signal, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import {
  LatLng,
  Map as LeafletMap,
  Marker,
  divIcon,
  marker,
} from "leaflet";
import { LeafletModule } from "@bluehalo/ngx-leaflet";
import { TranslocoPipe } from "@jsverse/transloco";
import { LorenDateModule } from "../../../../shared/pipes/loren-date/loren-date.module";
import { LorenUtilsService } from "../../../../shared/services/loren-utils-service/loren-utils.service";
import { LorenAsset } from "../../../loren-assets/interfaces/loren-asset.interface";
import { LorenAssetService } from "../../../loren-assets/services/loren-asset-service/loren-asset.service";
import { LorenLocation } from "../../../loren-assets/interfaces/loren-location.interface";
import {
    LorenAuthenticationService
} from "../../../../shared/services/loren-authentication-service/loren-authentication.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatButton } from "@angular/material/button";
import {getMapDefaults} from "../../../asset-map/map.defaults";

@Component({
  imports: [
    CommonModule,
    MatIcon,
    LeafletModule,
    TranslocoPipe,
    LorenDateModule,
    MatButton,
  ],
  selector: "loren-asset-details-map",
  styleUrls: ["./loren-asset-details-map.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: "./loren-asset-details-map.component.html",
})
export class LorenAssetDetailsMap implements OnInit, AfterViewInit {
  private assetService = inject(LorenAssetService);
  private authService = inject(LorenAuthenticationService);

  private readonly destroyRef = inject(DestroyRef);
  asset = input<LorenAsset>();

  private mapInstance: LeafletMap;
  private userId: string;
  options = getMapDefaults();

  mapFullSize = signal<boolean>(true);

    ngOnInit(): void {
        this.userId = this.authService.getUserInfo().id;
    }

  ngAfterViewInit() {
    this.mapFullSize.set(LorenUtilsService.getDashboardMapPreference(this.userId));
    this.showHideZoomControl();
  }

  toggleMapFullSize() {
    this.mapFullSize.update((v) => !v);
    this.showHideZoomControl();
    window.dispatchEvent(new Event("resize"));
    LorenUtilsService.setDashboardMapPreference(this.mapFullSize(), this.userId);
  }

  onMapReady(map: LeafletMap) {
    if (!map) {
      return;
    }
    this.mapInstance = map;

    setTimeout(() => {
      this.mapInstance.invalidateSize();
    });

    this.onMapLoaded();
  }

  private prepareAssetsMarkers() {
    const mapMarker: Marker = this.createNewMarker(
      this.asset().location,
      this.asset()
    );
    mapMarker.addTo(this.mapInstance);
    this.centerMarker();
  }

  centerMarker() {
    this.mapInstance.fitBounds([
      [this.asset().location.latitude, this.asset().location.longitude],
    ]);
  }

  private onMapLoaded(): void {
    this.assetService
      .getAssetImageObservable(this.asset().image)
      .pipe(
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        this.asset().avatar = res;
        this.prepareAssetsMarkers();
      });
  }

  private createNewMarker(
    lastLocation: LorenLocation,
    asset: LorenAsset,
    color: string = null,
    imgSrc: string = null
  ) {
    const image = imgSrc || asset.avatar || "/icon-96x96.png";
    const markerBackgroundColor = `background:` + color;
    const icon = divIcon({
      className: "custom-div-icon",
      html: `<div class='marker-pin' style=${
        color ? markerBackgroundColor : ""
      }></div><img src=${image}>`,
      iconSize: [30, 42],
      iconAnchor: [15, 42],
    });

    return marker(new LatLng(lastLocation.latitude, lastLocation.longitude), {
      icon,
    });
  }

  private showHideZoomControl() {
    const zoomControl = this.mapInstance.zoomControl;
    if(this.mapFullSize()) {
      this.mapInstance.addControl(zoomControl)
    } else {
      this.mapInstance.removeControl(zoomControl);
    }
  }
}
